import { useTranslation } from 'react-i18next'

import { ArcadeButtonLink, Flex } from '@cash-design-system/react'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { DownloadCashAppCTASubmitDownloadButton } from '@squareup/customer-data-events-web'

import { useUserAgent } from '../../hooks/useUserAgent'
import { isStaging } from '../../utils/env'
import {
  ANDROID_STORE,
  APP_DOWNLOAD_WITH_DEEP_LINK_PRODUCTION,
  APP_DOWNLOAD_WITH_DEEP_LINK_STAGING,
  APPLE_STORE,
} from '../../utils/outbound-urls'
import { AppleIcon } from './AppleIcon'
import { GoogleIcon } from './GoogleIcon'

type StoreIconType = 'apple' | 'google' | undefined
export type DownloadLabel = 'getApp' | 'contribute' | 'donate' | 'pay' | 'appstore' | 'playstore'

export type DownloadButtonProps = {
  pageName: string
  showAppStore?: boolean
  showPlayStore?: boolean
  campaign?: string
  overrideGetAppsUrl?: string
  showIcon?: boolean
  label?: DownloadLabel
  onClick?: () => void
} & Pick<React.ComponentProps<typeof ArcadeButtonLink>, 'prominence' | 'size'>

interface GetDestinationProps {
  campaign?: string
  isIOS: boolean
  isAndroid: boolean
  showAppStore?: boolean
  showPlayStore?: boolean
}

const getDestination = ({ campaign, isIOS, isAndroid, showAppStore, showPlayStore }: GetDestinationProps) => {
  if (!isIOS && !isAndroid && !showAppStore && !showPlayStore) {
    // If we're not on a supported device or directly linking to store, go to the homepage.
    return null
  }
  const suffix = campaign ? `-${campaign}` : ''

  // With integrating AppsFlyers deferred deeplink, user will be redirected to payByCashTag page
  // if a user downloads the app from the cashtag website
  // URL need to be encoded for Android to work

  // Campaign parameter is already included in the AppsFlyer redirect URL settings
  // https://hq1.appsflyer.com/onelink/link?linkId=88d62e23-a308-48d6-a882-3b397c6d7dcf
  // iOS -> https://itunes.apple.com/us/app/square-cash/id711923939?pt=302818&mt=8&ct=cash-web-cashtag-page
  // Android -> https://play.google.com/store/apps/details?id=com.squareup.cash&referrer=utm_medium%3Dweb%26utm_source%3Dcash-web-cashtag-page
  // Use deeplink if on iOS or Android
  if (isIOS || isAndroid) {
    // Staging only deeplink for testing purpose, replace http[s]:// protocol to cashme://
    if (isStaging()) {
      return (
        APP_DOWNLOAD_WITH_DEEP_LINK_STAGING +
        encodeURIComponent(window.location.href.replace(/^https?:\/\//i, 'cashme://'))
      )
    }

    return APP_DOWNLOAD_WITH_DEEP_LINK_PRODUCTION + encodeURIComponent(window.location.href)
  }

  return (showAppStore ? APPLE_STORE : ANDROID_STORE) + suffix
}

export const DownloadButton = ({
  prominence = 'prominent',
  size = 'cta',
  showIcon,
  showAppStore,
  showPlayStore,
  label = 'getApp',
  campaign,
  pageName,
  onClick,
}: DownloadButtonProps) => {
  const { t } = useTranslation()
  const { isIOS, isAndroid } = useUserAgent()
  const logCdfEvent = useLogCashWebCdfEvent()

  let iconType: StoreIconType = undefined
  if (showIcon) {
    // If a specific store is requested, show that icon.
    if (showAppStore) {
      iconType = 'apple'
    } else if (showPlayStore) {
      iconType = 'google'
    } else {
      // Otherwise, show the icon for the user's device.
      iconType = isIOS ? 'apple' : 'google'
    }
  }

  const destination = getDestination({ campaign, isIOS, isAndroid, showAppStore, showPlayStore }) ?? '/'
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    onClick?.()
    if (!destination) {
      // If we're not on a supported device, go to the homepage.
      window.location.href = '/'
      return
    }
    logCdfEvent(
      DownloadCashAppCTASubmitDownloadButton({
        pageName,
        ctaText: label,
        ctaUrl: destination,
      })
    )

    window.location.href = destination
  }

  const getButtonLabel = () => {
    switch (label) {
      case 'contribute':
        return t('Contribute with Cash App', { context: 'Button title to deep link into cash app to contribute' })
      case 'donate':
        return t('Donate with Cash App', { context: 'Button title to deep link into cash app and donate' })
      case 'pay':
        return t('Pay with Cash App', { context: 'Button title to deep link into cash app to pay someone' })
      case 'appstore':
        return t('App Store', { context: 'Apple App Store name on CTA' })
      case 'playstore':
        return t('Google Play', { context: 'Google Play Store name on CTA' })
      default:
        return t('Get the app', { context: 'Button title to download app.' })
    }
  }

  return (
    <ArcadeButtonLink
      aria-label={getButtonLabel()}
      href={destination}
      onClick={handleClick}
      prominence={prominence}
      size={size}
    >
      <Flex gap="xs" flexDirection="row" alignItems="center" justifyContent="center">
        {iconType && (iconType === 'apple' ? <AppleIcon /> : <GoogleIcon />)}
        {getButtonLabel()}
      </Flex>
    </ArcadeButtonLink>
  )
}
